import { Grid, Hidden } from "@material-ui/core";
import Button from "@material-ui/core/Button/Button";
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import Dialog from "@material-ui/core/Dialog/Dialog";
import DialogActions from "@material-ui/core/DialogActions/DialogActions";
import DialogContent from "@material-ui/core/DialogContent/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle/DialogTitle";
import Input from "@material-ui/core/Input/Input";
import Paper from "@material-ui/core/Paper/Paper";
import Table from "@material-ui/core/Table/Table";
import TableBody from "@material-ui/core/TableBody/TableBody";
import TableCell from "@material-ui/core/TableCell/TableCell";
import TableHead from "@material-ui/core/TableHead/TableHead";
import TableRow from "@material-ui/core/TableRow/TableRow";
import TextField from "@material-ui/core/TextField/TextField";
import Toolbar from "@material-ui/core/Toolbar/Toolbar";
import Typography from "@material-ui/core/Typography/Typography";
import Add from "@material-ui/icons/Add";
import Cancel from "@material-ui/icons/Cancel";
import Delete from "@material-ui/icons/Delete";
import Edit from "@material-ui/icons/Edit";
import Save from "@material-ui/icons/Save";
import * as React from "react";
import { RouteComponentProps } from "react-router-dom";
import { IAuthContextProps, withAuthContext } from "../components/with-auth-context";
import { ISnackbarContextProps, withSnackbar } from "../components/with-snackbar";
import { CreateTagModel } from "../model/autogen/CreateTagModel";
import { TagRequestFactory } from "../model/autogen/TagController";
import { TagModel } from "../model/autogen/TagModel";
import { UserLevel } from "../model/autogen/UserLevel";
import { WithDefaultStyles, withDefaultStyles } from "../utils/createStyles";
import { NBSP } from "../utils/string";
import { IRouteMatch } from "./editor-page";

export interface ITagManagementPageComponentProps {
    resourceOnly: boolean;
}

export type TagManagementPageComponentProps = ITagManagementPageComponentProps & RouteComponentProps<IRouteMatch> & IAuthContextProps & ISnackbarContextProps & WithDefaultStyles;

export class TagManagementPageComponent extends React.Component<TagManagementPageComponentProps, any> {
    constructor(props: TagManagementPageComponentProps) {
        super(props);
        this.state = {
            tags: undefined,
            addingNewTag: false,
            processingTagId: undefined,
            editingTagId: undefined,
            editingTagName: undefined,
            isAdding: false
        };
    }
    async componentDidMount() {
        try {
            this.setState({ tags: undefined });
            const tags = await this.props.callApi(f => TagRequestFactory.list(f, this.props.resourceOnly));
            this.setState({ tags: tags.sort((a: TagModel, b: TagModel) => a.name.localeCompare(b.name)) });
        } catch (e) {
            this.setState({ tags: undefined });
        }
    }
    private onDeleteTag = async (e: any) => {
        try {
            const id = e.currentTarget.getAttribute("data-id");
            const tag = this.state.tags.filter((t: TagModel) => t.id == id)[0];
            if (tag && confirm("Are you sure you want to delete this tag? Any existing content tagged with this tag will be un-tagged")) {
                this.setState({ processingTagId: id });
                const deleted = await this.props.callApi(f => TagRequestFactory.delete(f, tag));
                if (deleted) {
                    this.props.showSnackbarMessage(`Deleted tag: ${tag.name}`);
                    this.setState({ tags: this.state.tags.filter((t: TagModel) => t.id != id) });
                }
                this.setState({ processingTagId: undefined });
            }
        } catch (e) {
            this.props.showSnackbarMessage("Error deleting tag");
            this.setState({ processingTagId: undefined });
        }
    }
    private onBeginEditTag = (e: any) => {
        const id = e.currentTarget.getAttribute("data-id");
        const t = this.state.tags.filter((t: TagModel) => t.id == id)[0];
        if (t) {
            this.setState({ editingTagId: id, editingTagName: t.name });
        } else {
            alert("Can't find tag to start editing");
        }
    }
    private endEditTag = async (e: any) => {
        try {
            this.setState({ processingTagId: this.state.editingTagId });
            const tag = this.state.tags.filter((t: TagModel) => t.id == this.state.editingTagId)[0];
            const payload = { ...tag, ... { name: this.state.editingTagName } };
            const result = await this.props.callApi(f => TagRequestFactory.update(f, payload));
            const newTags = [...(this.state.tags.filter((t: TagModel) => t.id != this.state.editingTagId)), result].sort((a: TagModel, b: TagModel) => a.name.localeCompare(b.name));
            this.props.showSnackbarMessage("Tag updated");
            this.setState({ editingTagId: undefined, processingTagId: undefined, tags: newTags });
        } catch (e) {
            this.props.showSnackbarMessage("Error updating tag");
        }
    }
    private onEditingTagNameChange = (e: any) => {
        this.setState({ editingTagName: e.target.value });
    }
    private onCancelEditingTag = (e: any) => {
        this.setState({ editingTagId: undefined, editingTagName: undefined });
    }
    private onAddNewTag = (e: any) => {
        this.setState({ addingNewTag: true });
    }
    private onCancelAddTag = (e: any) => {
        this.setState({ addingNewTag: false, editingTagName: undefined });
    }
    private onSubmitNewTag = async (e: any) => {
        const { editingTagName } = this.state;
        this.setState({ isAdding: true });
        const payload = {
            name: editingTagName,
            isResourceOnly: this.props.resourceOnly
        } as CreateTagModel;
        const result = await this.props.callApi(f => TagRequestFactory.create(f, payload));
        if (result.error) {
            this.setState({ saveError: result.error, isAdding: false });
        } else if (result.tag) {
            const newTags = [...(this.state.tags.filter((t: TagModel) => t.id != this.state.editingTagId)), result.tag].sort((a: TagModel, b: TagModel) => a.name.localeCompare(b.name));
            this.props.showSnackbarMessage(`Saved tag (${result.tag.name})`);
            this.setState({ saveError: undefined, isAdding: false, addingNewTag: false, editingTagName: undefined, tags: newTags });
        }
    }
    render(): JSX.Element {
        const { classes } = this.props;
        const { isAdding, tags, processingTagId, editingTagId, editingTagName, addingNewTag, saveError } = this.state;
        const isProcessing = processingTagId != null;

        const sideGabs = <Hidden mdDown>
            <Grid item lg={2}></Grid>
        </Hidden>

        if (tags) {
            return <div className={classes.container}><Grid  container spacing={24}>
                {sideGabs}
                <Grid item xs>
                    <Paper style={{ width: '100%' }}>
                        <Toolbar style={{float:'right'}}>
                            <Button variant="contained" color="primary" disabled={isProcessing} onClick={this.onAddNewTag}><Add /> Add New Tag</Button>
                        </Toolbar>
                        <Table style={{ width: '100%' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ textAlign: 'left', width: '10%' }} numeric>ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell style={{ width: '30%' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tags.map((t: TagModel) => (<TableRow key={t.id}>
                                    {(() => {
                                        if (t.id == processingTagId) {
                                            return <TableCell colSpan={3}><CircularProgress size={30} /> Processing action ...</TableCell>;
                                        } else {
                                            return <>
                                                <TableCell style={{ textAlign: 'left', width: '10%' }} numeric>{t.id}</TableCell>
                                                <TableCell component="th" scope="row">
                                                    {t.id == editingTagId ? <Input value={editingTagName} fullWidth onChange={this.onEditingTagNameChange} /> : <>{t.name}</>}
                                                </TableCell>
                                                <TableCell>
                                                    {t.id == editingTagId ? <>
                                                        <Button variant="contained" color="primary" disabled={isProcessing} onClick={this.endEditTag}><Save /> Finish</Button>
                                                        {NBSP}
                                                        <Button variant="contained" color="secondary" disabled={isProcessing} data-id={t.id} onClick={this.onCancelEditingTag}><Cancel /> Cancel</Button>
                                                    </> : <>
                                                            <Button variant="contained" color="primary" disabled={isProcessing} data-id={t.id} onClick={this.onBeginEditTag}><Edit /> Edit</Button>
                                                            {NBSP}
                                                            <Button variant="contained" color="secondary" disabled={isProcessing} data-id={t.id} onClick={this.onDeleteTag}><Delete /> Delete</Button>
                                                        </>}
                                                </TableCell>
                                            </>;
                                        }
                                    })()}
                                </TableRow>))}
                            </TableBody>
                        </Table>
                        <Dialog open={addingNewTag} onClose={this.onCancelAddTag} disableBackdropClick={true}>
                            <DialogTitle id="form-dialog-title">Create New Tag</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Enter the name of the tag. Upon saving, this tag will be available to tag any content with.
                            {saveError && <Typography className="tag-save-error" variant="subtitle1" gutterBottom color="error">
                                        {saveError}
                                    </Typography>}
                                </DialogContentText>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    id="tag"
                                    label="Tag Name"
                                    type="text"
                                    value={this.state.editingTagName}
                                    onChange={this.onEditingTagNameChange}
                                    fullWidth
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button disabled={isAdding} onClick={this.onSubmitNewTag} color="primary">
                                    Create
                                </Button>
                                <Button disabled={isAdding} onClick={this.onCancelAddTag} color="secondary">
                                    Cancel
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Paper>
                </Grid>
                {sideGabs}
            </Grid>
            </div>;
        } else {
            return <CircularProgress size={50} />;
        }
    }
}

const TagManagementPageComponentWithStyles = withDefaultStyles(TagManagementPageComponent);

const TagManagementPageBase = withSnackbar(withAuthContext(TagManagementPageComponentWithStyles, UserLevel.Editor) as any);

export const TagManagementPage = (props: any) => <TagManagementPageBase resourceOnly={false} {...props} />

export const ResourceOnlyTagManagementPage = (props: any) => <TagManagementPageBase resourceOnly {...props} />


// WEBPACK FOOTER //
// ./src/pages/tag-management-page.tsx